import React, { useEffect, useState } from "react";
import axios from "axios";
import { Mail, RefreshCw, AlertCircle, Trash2 } from "lucide-react";
import { useLang } from "../LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminContactMessages() {
  const { t, lang } = useLang();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setError("");
    try {
      const { data } = await axios.get(`${API}/contact`, { withCredentials: true });
      setItems(data);
    } catch {
      setError(t("admin_contact.load_error"));
      setItems([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subjectLabel = (key) => t(`admin_contact.subjects.${key}`) || key;

  const remove = async (id) => {
    if (!window.confirm(t("admin_contact.confirm_delete"))) return;
    await axios.delete(`${API}/contact/${id}`, { withCredentials: true });
    setItems((prev) => prev.filter((m) => m.id !== id));
  };

  return (
    <section className="mt-14" data-testid="admin-contact-messages">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <p className="eyebrow mb-1">{t("admin_contact.eyebrow")}</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-ink">
            {t("admin_contact.title")}
          </h2>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-forest"
          data-testid="admin-contact-refresh"
        >
          <RefreshCw size={15} /> {t("admin_contact.refresh")}
        </button>
      </div>

      {error && (
        <div
          className="rounded-2xl bg-red-50 border border-red-200 text-red-800 px-4 py-2.5 text-sm flex items-center gap-2 mb-4"
          data-testid="admin-contact-error"
        >
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {!items ? (
        <div className="text-sm text-ink-muted">…</div>
      ) : items.length === 0 ? (
        <div className="card-soft p-6 text-sm text-ink-muted">
          {t("admin_contact.empty")}
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((m, i) => {
            const open = openId === m.id;
            return (
              <li
                key={m.id}
                className="card-soft p-5"
                data-testid={`admin-contact-item-${i}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : m.id)}
                  className="w-full flex items-start gap-4 text-left"
                  data-testid={`admin-contact-toggle-${i}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-sage-light grid place-items-center text-forest-dark shrink-0">
                    <Mail size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-ink-muted mb-1">
                      <span>
                        {new Date(m.created_at).toLocaleDateString(
                          lang === "fr" ? "fr-FR" : "en-GB",
                          { day: "numeric", month: "short", year: "numeric" }
                        )}
                      </span>
                      <span>·</span>
                      <span className="rounded-full px-2 py-0.5 bg-forest-light/70 text-forest-deep font-medium">
                        {subjectLabel(m.subject)}
                      </span>
                    </div>
                    <div className="font-heading text-base font-semibold text-ink truncate">
                      {m.name}{" "}
                      <span className="text-ink-muted font-normal text-sm">· {m.email}</span>
                    </div>
                    {!open && (
                      <div className="text-sm text-ink-muted line-clamp-1">{m.message}</div>
                    )}
                  </div>
                </button>
                {open && (
                  <div
                    className="mt-4 pl-14 text-sm text-ink leading-relaxed whitespace-pre-wrap"
                    data-testid={`admin-contact-body-${i}`}
                  >
                    {m.message}
                    <div className="mt-3 flex items-center gap-4">
                      <a
                        href={`mailto:${m.email}?subject=${encodeURIComponent("Re: " + subjectLabel(m.subject))}`}
                        className="btn-ghost text-sm"
                        data-testid={`admin-contact-reply-${i}`}
                      >
                        {t("admin_contact.reply")}
                      </a>
                      <button
                        type="button"
                        onClick={() => remove(m.id)}
                        className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-red-700"
                        data-testid={`admin-contact-delete-${i}`}
                      >
                        <Trash2 size={14} /> {t("admin_contact.delete")}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
